import {lazy, Suspense, useEffect, useRef, useState, type FormEvent, type ReactNode} from 'react';
import {Empty, ErrorNotice, Loading, ResourceView, shortId} from './components';
import {getTeamRole, type TeamRoleId} from '../src/team';
import {PRODUCT_ENTRY_LABEL} from '../src/product';
import {AgentCostReadout, NativeTeamTaskList, TeamUsageSummary, nativeTeamProjection} from './team-workspace';
import {WorkspaceShell, WorkspaceSidebar, type WorkspacePage} from './workspace-ui';
import {useHermes, errorDetails} from './lib/store';
import {nativeMessageAccepted} from './lib/native-message-delivery';
import {workspaceContextQuery} from './lib/workspace-location';
import {NativeQuestion} from './native-question';
import {AccountIdentity} from './auth';
import type {ErrorDetails, NativeSessionDetail, HermesState} from './lib/types';
import {APP_BASE, Link, navigate, usePath, useSearch} from './router';
import {OrgChart} from './org-chart';
import {ChatMarkdown} from './chat-markdown';
import {conversationMessages, messageDisplayPrefix, userMessageDisplayText} from './lib/conversation-messages';
import {BotSessionTokens, ConversationCost} from './workspace-usage';
import {WhatsAppDialog} from './whatsapp-dialog';

const NativeTerminal = lazy(() => import('./native-terminal').then(module => ({default: module.NativeTerminal})));

type Actions = ReturnType<typeof useHermes>['actions'];
type Props = {state: HermesState; actions: Actions};
type ConversationView = 'conversation' | 'terminal';
const messageTime = new Intl.DateTimeFormat(undefined, {weekday: 'long', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'});

function retainChatInUrl(sessionId: string): void {
  const url = new URL(window.location.href);
  if (url.searchParams.has('chat')) return;
  url.searchParams.set('chat', sessionId);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function MessageDisplayStatus({state}: {state: HermesState}) {
  if (state.nativeConnection.status === 'error') return null;
  if (state.accountSettings.error) return <ErrorNotice error={state.accountSettings.error}/>;
  if (state.accountSettings.status === 'ready' && state.nativeConnection.status !== 'connecting') {
    return <p className="field-help">Message settings are unavailable. Reconnect to your work chat.</p>;
  }
  return <Loading/>;
}

function NativeTranscript({history, liveTurn = null, agentName, messagePrefix}: {
  history: readonly Record<string, unknown>[]; liveTurn?: NativeSessionDetail['liveTurn']; agentName: string;
  messagePrefix: string;
}) {
  const messages = conversationMessages(history, messagePrefix, liveTurn);
  let previousDay: string | null = null;
  return <div className="chat-history native-transcript" aria-label="Recorded conversation">
    {messages.length === 0 && !liveTurn ? <div className="muted"><p>Send a message to {agentName}.</p></div> : messages.map(message => {
      const date = message.timestamp === null ? null : new Date(message.timestamp * 1000);
      if (date && !Number.isFinite(date.getTime())) throw new Error(`Invalid message timestamp: ${message.timestamp}`);
      const day = date?.toDateString() ?? null;
      const showDate = day !== null && day !== previousDay;
      if (day !== null) previousDay = day;
      return <article key={message.key} className={message.role === 'user' ? 'user-message' : 'agent-response'}
        aria-label={message.role === 'user' ? 'Your message' : `${agentName} reply`}>
        {showDate && date && <time className="chat-message-time" dateTime={date.toISOString()}>
          {messageTime.format(date)}</time>}
        <span className="visually-hidden">{message.role === 'user' ? 'You' : agentName}</span>
        {message.text && (message.role === 'user' ? <p>{message.text}</p> : <ChatMarkdown text={message.text}/>)}
        {message.unprocessedCorrection && <p className="field-help">This message was not processed during that turn.</p>}
      </article>;
    })}
  </div>;
}

export function RuntimeSettings({state, actions}: Props) {
  const runtime = state.nativeRuntime.data;
  const provisioning=state.accountSettings.data?.provisioning;
  return <section className="native-runtime-settings"><ErrorNotice error={state.nativeRuntime.error}/>
    <Loading active={provisioning?.state==='pending'||provisioning?.state==='running'}/>
    {provisioning?.state==='error' && <><ErrorNotice error={provisioning.error as import('./lib/types').ErrorDetails}/><button className="button" disabled={state.operation.label!==null} onClick={()=>void actions.prepareAccount().catch(()=>undefined)}>Retry team setup</button></>}
    {!runtime && state.nativeRuntime.status === 'loading' && <Loading/>}
    {runtime && <><h2>Your team</h2>
      <p className="native-runtime-status">{runtime.configured
        ? <>{runtime.running ? 'Your team is running.' : 'Your team is stopped.'}</>
        : `Select “${PRODUCT_ENTRY_LABEL}” to give your team a task. Your files and conversations stay in your account.`}</p>
      {!state.accountSettings.data?.modelKey.configured && <p className="field-help">Contact your administrator to configure model access before you select “{PRODUCT_ENTRY_LABEL}”.</p>}
      {!runtime.running && <button type="button" className="button" disabled={state.operation.label !== null || !state.accountSettings.data?.modelKey.configured}
        onClick={() => void actions.configureHermes().catch(() => undefined)}>{PRODUCT_ENTRY_LABEL}</button>}
    </>}
  </section>;
}

function Conversation({state, actions, agentName, view, setView, draft, setDraft, selectionReady}: Props & {
  agentName: string; view: ConversationView;
  setView: (view: ConversationView) => void;
  draft: string; setDraft: (text: string) => void; selectionReady: boolean;
}) {
  const [deliveryError, setDeliveryError] = useState<ErrorDetails | null>(null);
  const body = useRef<HTMLDivElement>(null);
  const [follow, setFollow] = useState(true);
  const mounted = useRef(false);
  const detail = state.nativeDetail.data;
  const selected = state.nativeSelectedAgentId;
  const child = selected !== null && state.nativeAgent.data?.agent.sessionId === selected ? state.nativeAgent.data : null;
  const agent = selected === null ? null : detail?.agents.find(item => item.sessionId === selected);
  const history = !selectionReady ? undefined : selected === null ? detail?.displayHistory : child?.displayHistory;
  const messagePrefix = messageDisplayPrefix(state);
  const connected = selectionReady && state.nativeConnection.status === 'open' && state.nativeTarget?.sessionId === detail?.session.sessionId;
  const execution = selected === null ? detail?.state : agent;
  const busy = state.operation.label !== null || !connected || state.nativeDetail.status === 'error' || history === undefined || execution === undefined || messagePrefix === null;
  const working = connected && execution?.running === true;
  const resuming = connected && execution?.resuming === true && !working;
  const liveTurn = selected === null && connected ? detail?.liveTurn ?? null : null;
  const requests = connected ? detail?.openRequests.filter(request => {
    const owner = record(request.params)?.session_id;
    return typeof owner === 'string' && (selected === null
      ? owner === detail.session.sessionId || owner === detail.state.liveSessionId : owner === selected);
  }) ?? [] : [];
  const result = state.nativeMessageResult.data;
  useEffect(() => {
    if (view !== 'conversation' || !follow || !body.current) return;
    const container = body.current;
    const scroll = () => {container.scrollTop = container.scrollHeight;};
    scroll();
    const observer = new ResizeObserver(scroll);
    observer.observe(container);
    for (const child of container.children) observer.observe(child);
    return () => observer.disconnect();
  }, [history, liveTurn, requests.length, working, resuming, follow, view]);
  useEffect(() => {
    mounted.current = true;
    return () => {mounted.current = false;};
  }, []);
  async function send(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (busy || resuming || !draft.trim()) return;
    setDeliveryError(null);
    try {
      const sent = await actions.sendNativeMessage(draft);
      if (nativeMessageAccepted(sent)) {
        setDraft('');
        if (mounted.current) {
          setView('conversation');
          setFollow(true);
        }
      }
    } catch (error) {if (mounted.current) setDeliveryError(errorDetails(error));}
  }
  return <div className="native-conversation-panel" hidden={view !== 'conversation'}><div className="conversation-body" ref={body} onScroll={event => {
    if (view !== 'conversation') return;
    const node = event.currentTarget;
    setFollow(node.scrollHeight - node.scrollTop - node.clientHeight < 48);
  }}>
    {messagePrefix === null ? <MessageDisplayStatus state={state}/>
      : history === undefined ? <Loading active={state.nativeConnection.status !== 'error'}/>
      : <NativeTranscript history={history} agentName={agentName} liveTurn={liveTurn} messagePrefix={messagePrefix}/>}
    <ErrorNotice error={liveTurn?.error ? {name: 'AgentError', message: liveTurn.error, cause: liveTurn.error_surface} : null}/>
    {execution?.error && execution.error !== liveTurn?.error && <ErrorNotice error={{name: 'AgentError', message: execution.error}}/>}
    {messagePrefix !== null && requests.map(request => <NativeQuestion key={String(request.id)} request={request}
      answer={actions.answerNativeRequest} busy={busy} messagePrefix={messagePrefix}/>)}</div>
    {view === 'conversation' && !follow && <button type="button" className="text-button chat-latest" onClick={() => setFollow(true)}>Latest messages</button>}
    <form className="chat-composer" onSubmit={event => void send(event)}>
      <label className="field"><span className="visually-hidden">Message {agentName}</span>
      <textarea value={draft} onChange={event => setDraft(event.target.value)} rows={2} disabled={state.operation.label !== null}
        onKeyDown={event => {
          if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing &&
              !window.matchMedia('(hover:none) and (pointer:coarse)').matches) {
            event.preventDefault();
            if (!busy && !resuming && draft.trim()) event.currentTarget.form?.requestSubmit();
          }
        }} placeholder={`Message ${agentName}…`} required/></label>
      <div className="button-row"><span className="chat-keyboard-hint">Shift + Enter for a new line</span>
        <button type="submit" className="button" disabled={busy || resuming || !draft.trim()}>Send</button>
        {selected !== null && working && <button type="button" className="text-button" disabled={busy}
          onClick={() => void actions.interruptNativeAgent().catch(() => undefined)}>Stop agent</button>}</div>
      <ErrorNotice error={deliveryError}/>
      {result?.status === 'rejected' && <div className="native-chat-notice" role="alert"><p>Your message was not accepted. Your draft is kept.</p>
        <details><summary>Full error details</summary><pre>{JSON.stringify(result, null, 2)}</pre></details></div>}
    </form></div>;
}

export function NativeChat({state, actions, requestedChatId, requestedAgentId}: Props & {
  requestedChatId: string | null; requestedAgentId: string | null;
}) {
  const [view, setView] = useState<ConversationView>('conversation');
  const [whatsappOpen, setWhatsAppOpen] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const defaultChatId = useRef<string | null>(null);
  const selected = state.nativeSelectedAgentId;
  const detail = state.nativeDetail.data;
  // The server resolves the default chat after the composer is already usable.
  // Keep that draft when its real ID is inserted into the URL. Other chats and
  // agents have separate, document-local drafts; none are sent during reconnect.
  if (requestedChatId === null && detail) defaultChatId.current = detail.session.sessionId;
  const draftChat = requestedChatId === null || requestedChatId === defaultChatId.current ? 'default' : requestedChatId;
  const draftKey = JSON.stringify([draftChat, requestedAgentId]);
  const selectionReady = requestedAgentId === selected && detail !== null &&
    (requestedChatId === null || requestedChatId === detail.session.sessionId);
  const accountId = state.session.data?.accountId;
  const agent = selected === null ? null : detail?.agents.find(item => item.sessionId === selected);
  const roleId = selected === null ? 'head' : agent?.roleId;
  const role = roleId ? getTeamRole(roleId) : null;
  const messagePrefix = messageDisplayPrefix(state);
  const agentName = role?.name ?? (messagePrefix !== null && agent?.title ? userMessageDisplayText(agent.title, messagePrefix, {folded: true}) : undefined)
    ?? (selected === null ? getTeamRole('head').name : shortId(selected));
  return <section className="agent-chat" aria-label={`${agentName} conversation`}><div className="chat-heading">
    <div className="view-switch" aria-label="Conversation view"><button type="button" aria-pressed={view === 'conversation'} onClick={() => setView('conversation')}>Chat</button>
      <button type="button" aria-pressed={view === 'terminal'} onClick={() => setView('terminal')}>Terminal</button>
      <button type="button" className="whatsapp-launch" aria-haspopup="dialog" onClick={() => setWhatsAppOpen(true)}>
        <img src={`${APP_BASE}/assets/whatsapp-official.svg`} width={23} height={23} alt=""/>WhatsApp</button></div></div>
    {agent && !role && messagePrefix !== null && <p className="native-agent-task">{userMessageDisplayText(agent.goal, messagePrefix)}</p>}
    {agent && !role && <AgentCostReadout usage={state.nativeConnection.status === 'open' && state.nativeDetail.status !== 'error' ? agent.usage : null}/>}
    {selected !== null && <ErrorNotice error={state.nativeAgent.error}/>}
    <Conversation key={draftKey} state={state} actions={actions} agentName={agentName} view={view} setView={setView}
      draft={drafts[draftKey] ?? ''} setDraft={text => setDrafts(previous => ({...previous, [draftKey]: text}))}
      selectionReady={selectionReady}/>
    {view === 'terminal' && (!accountId || !detail || !selectionReady ? <Loading active={state.nativeConnection.status !== 'error'}/>
        : <Suspense fallback={<Loading/>}><NativeTerminal key={JSON.stringify([accountId, detail.session.sessionId, selected])} accountId={accountId}
          sessionId={detail.session.sessionId} agentId={selected} openTerminal={actions.openNativeTerminal}/></Suspense>)}
    {whatsappOpen && <WhatsAppDialog key={JSON.stringify([accountId, detail?.session.sessionId])} state={state} actions={actions}
      sessionId={detail?.session.sessionId} onClose={() => setWhatsAppOpen(false)}/>}
  </section>;
}

function WorkChatSidebar({state, actions, selectedChatId, onSelect, onCreate, onClose, onNavigate}: Props & {
  selectedChatId: string | null; onSelect: (id: string) => void; onCreate: (title?: string) => Promise<boolean>;
  onClose: () => void; onNavigate: () => void;
}) {
  const search = useSearch();
  const path = usePath();
  const query = workspaceContextQuery(search);
  const settingsHref = `${APP_BASE}/settings${query}`;
  const busy = state.operation.label !== null;
  const enabled = state.nativeRuntime.data?.configured === true && state.accountSettings.data?.modelKey.configured === true;
  const messagePrefix = messageDisplayPrefix(state);
  const page: WorkspacePage = path.endsWith('/settings') ? 'settings' : path.endsWith('/files') ? 'files' : path.endsWith('/tasks') ? 'tasks' : path.endsWith('/agents') ? 'team' : 'workspace';
  const rows = state.nativeSessions.status === 'error' || messagePrefix === null ? [] : (state.nativeSessions.data ?? []).map(item => ({
    id: item.sessionId, title: userMessageDisplayText(item.title?.trim() ?? '', messagePrefix, {folded: true}) || 'Chat title unavailable',
    preview: item.preview === null ? null : userMessageDisplayText(item.preview, messagePrefix, {folded: true}), updatedAt: item.updatedAt
  })).sort((a, b) => (b.updatedAt === null ? 0 : new Date(b.updatedAt).getTime()) - (a.updatedAt === null ? 0 : new Date(a.updatedAt).getTime()));
  return <WorkspaceSidebar chats={rows} selectedChatId={selectedChatId} onSelect={onSelect} onCreate={() => onCreate()}
    busy={busy} createDisabled={!enabled} loading={state.nativeSessions.status === 'loading'} page={page}
    settingsHref={settingsHref} onClose={onClose} onNavigate={onNavigate}
    status={<><ErrorNotice error={state.nativeSessions.error}/>{messagePrefix === null && <MessageDisplayStatus state={state}/>}</>}
    footer={<><ConversationCost state={state} sessionId={selectedChatId}/>
      <AccountIdentity state={state} actions={actions}/></>}/>;
}

export function NativeWorkspace({state, actions, view = 'workspace', children}: Props & {view?: WorkspacePage; children?: ReactNode}) {
  const path = usePath();
  const search = useSearch();
  const parameters = new URLSearchParams(search);
  const requestedChatId = parameters.get('chat');
  const requestedRole = parameters.get('role');
  if (requestedRole) getTeamRole(requestedRole);
  const settingsUrl = `${APP_BASE}/settings${workspaceContextQuery(search)}`;
  const accountId = state.session.data?.accountId;
  const configured = state.nativeRuntime.data?.configured === true;
  const keyConfigured = state.accountSettings.data?.modelKey.configured === true;
  const opened = state.nativeDetail.data;
  const openedRequestedChat = opened !== null && (requestedChatId === null || opened.session.sessionId === requestedChatId);
  const selectedFromUrl = parameters.has('agent') ? parameters.get('agent')
    : requestedRole && requestedRole !== 'head' ? opened?.agents.find(agent => agent.roleId === getTeamRole(requestedRole).id)?.sessionId ?? null : null;
  const chat = useRef<HTMLDivElement>(null);
  const pendingReview = useRef<string | null>(null);
  const [reviewVersion, setReviewVersion] = useState(0);
  const [reviewError, setReviewError] = useState<ErrorDetails | null>(null);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {mounted.current = false;};
  }, []);
  // The URL owns the requested work chat. The store owns each active request and
  // server response; changing the URL invalidates the preceding poll generation.
  useEffect(() => actions.closeHermes, [accountId, actions]);
  useEffect(() => {
    if (!accountId) return;
    if (state.nativeConnection.status === 'connecting' && state.nativeTarget?.sessionId === requestedChatId) return;
    let current = true;
    void actions.openHermes(requestedChatId).then(mapping => {
      if (!current || !mapping || requestedChatId !== null) return;
      retainChatInUrl(mapping.sessionId);
    }).catch(() => undefined);
    return () => {current = false;};
  }, [accountId, requestedChatId, actions]);
  useEffect(() => {
    if (!openedRequestedChat || state.nativeSelectedAgentId === selectedFromUrl) return;
    void actions.selectNativeAgent(selectedFromUrl).catch(() => undefined);
  }, [openedRequestedChat, opened?.session.sessionId, selectedFromUrl, state.nativeSelectedAgentId, actions]);
  useEffect(() => {
    const requested = pendingReview.current;
    if (!requested || !opened) return;
    const currentSession = state.nativeSelectedAgentId ?? opened.session.sessionId;
    if (currentSession !== requested) return;
    const input = chat.current?.querySelector<HTMLElement>('.native-question input, .native-question textarea, .native-question button');
    if (!input) return;
    input.focus({preventScroll: true});
    pendingReview.current = null;
  }, [reviewVersion, state.nativeSelectedAgentId, state.nativeAgent.status, opened]);
  async function refreshWorkspace(): Promise<void> {
    const mapping = await actions.refreshHermes(requestedChatId);
    if (mounted.current && mapping && requestedChatId === null) retainChatInUrl(mapping.sessionId);
  }
  function selectChat(id: string): void {
    if (id !== opened?.session.sessionId) actions.closeHermes();
    const params = new URLSearchParams(search);
    params.set('chat', id); params.delete('agent'); params.delete('role');
    navigate(`${APP_BASE}/workspace?${params}`);
  }
  async function createChat(title?: string): Promise<boolean> {
    try {
      const created = await actions.createNativeChat(title);
      if (!mounted.current) return false;
      selectChat(created.sessionId);
      return true;
    } catch {return false;}
  }
  function selectAgent(id: string | null): void {
    if (!openedRequestedChat || !opened) return;
    const params = new URLSearchParams(search);
    params.set('chat', opened.session.sessionId); params.delete('role');
    if (id === null || id === opened.session.sessionId) params.delete('agent'); else params.set('agent', id);
    navigate(`${view === 'team' ? `${APP_BASE}/workspace` : path}?${params}`);
  }
  function selectRole(roleId: TeamRoleId): void {
    if (roleId === 'head') {selectAgent(null); return;}
    const agent = opened?.agents.find(item => item.roleId === roleId);
    if (!agent) throw new Error(`The ${getTeamRole(roleId).name} conversation is missing from your team.`);
    selectAgent(agent.sessionId);
  }
  const projection = nativeTeamProjection(state);
  const messagePrefix = messageDisplayPrefix(state);
  const workspaceErrors = [...new Set(state.nativeConnection.error ? [state.nativeConnection.error]
    : [state.accountSettings.error, state.nativeRuntime.error, state.nativeDetail.error, projection.error]
      .filter((error): error is ErrorDetails => error !== null))];
  const unavailableChat = requestedChatId !== null && state.nativeConnection.error?.status === 404;
  function reviewRequest(sessionId: string): void {
    const isHead = sessionId === opened?.session.sessionId || sessionId === opened?.state.liveSessionId;
    if (!isHead && !opened?.agents.some(agent => agent.sessionId === sessionId)) {
      setReviewError(errorDetails(new Error(`The request belongs to native session ${sessionId}, which is not present in the selected chat.`)));
      return;
    }
    setReviewError(null);
    pendingReview.current = isHead ? opened!.session.sessionId : sessionId;
    setReviewVersion(value => value + 1);
    selectAgent(isHead ? null : sessionId);
  }
  const conversation = !children && view !== 'team' && accountId ? <aside className="workspace-conversation" ref={chat} aria-label="Selected agent conversation">
    <ErrorNotice error={reviewError}/>
    <NativeChat key={accountId} state={state} actions={actions} requestedChatId={requestedChatId} requestedAgentId={selectedFromUrl}/>
  </aside> : undefined;
  return <WorkspaceShell sidebar={controls => <WorkChatSidebar state={state} actions={actions}
    selectedChatId={requestedChatId ?? opened?.session.sessionId ?? null} onSelect={selectChat} onCreate={createChat} {...controls}/>}
    conversation={conversation} conversationInMain={view === 'workspace'} className={`native-workspace${view === 'team' ? ' is-chart-page' : view === 'workspace' ? ' is-chat-page' : ''}`}>
    {!children && (workspaceErrors.length > 0 || (configured && keyConfigured && state.nativeConnection.status === 'closed')) && <>
      <div className="workspace-connection-status">
        {unavailableChat ? <div role="alert"><p>This chat is unavailable in your signed-in account. Choose one of your work chats, or open your account's default chat.</p>
          <button type="button" className="button" disabled={state.operation.label !== null}
            onClick={() => {actions.closeHermes(); navigate(`${APP_BASE}/workspace`, true);}}>Open my work chat</button></div>
          : <button type="button" className="text-button" disabled={state.operation.label !== null || state.nativeConnection.status === 'connecting'}
            onClick={() => void refreshWorkspace().catch(() => undefined)}>Reconnect</button>}</div>
      {workspaceErrors.map((error, index) => <ErrorNotice key={index} error={error}/>)}</>}
    {children ? <div className="workspace-secondary-content">{children}</div>
      : workspaceErrors.length > 0 ? null
      : !state.accountSettings.data ? <Loading/>
      : !keyConfigured ? <div className="team-workspace-setup"><Empty title="Set up your team."
        action={<Link className="button" href={settingsUrl}>Open Settings</Link>}>Contact your administrator to configure your team's model connection.</Empty></div>
      : !configured ? <div className="team-workspace-setup"><RuntimeSettings state={state} actions={actions}/></div>
      : <ResourceView resource={state.nativeDetail}>{detail => <>
          {view === 'team' ? <section className="workspace-chart-page" aria-label="Your team">
            <OrgChart projection={projection} selectedSessionId={state.nativeSelectedAgentId} onSelect={selectAgent}
              renderUsage={member => <BotSessionTokens state={state} member={member}/>}/></section>
          : view === 'tasks' ? <div className="workspace-tasks-page"><section className="workspace-task-panel" aria-label="Work in this chat"><NativeTeamTaskList state={state} projection={projection} onSelect={selectRole} onSelectSession={selectAgent} onReview={reviewRequest}/></section>
          {messagePrefix !== null && detail.agents.some(agent => agent.roleId === null) && <details className="workspace-support-details"><summary>Child agents and saved work</summary>
            <div className="native-session-list">{detail.agents.filter(agent => agent.roleId === null).map(agent =>
              <div className="team-extra-session" key={agent.sessionId}><button type="button" className={`session-row ${state.nativeSelectedAgentId === agent.sessionId ? 'selected' : ''}`} onClick={() => selectAgent(agent.sessionId)}>
                <span><strong>{agent.title === null ? shortId(agent.sessionId) : userMessageDisplayText(agent.title, messagePrefix, {folded: true})}</strong>
                  <small>{userMessageDisplayText(agent.goal, messagePrefix)}</small>
                  <small>{agent.executionKind === 'scheduled' ? 'Scheduled agent' : 'Child agent'}{agent.currentTool && ` · ${agent.currentTool}`}</small></span>
                <span className={`session-status ${agent.running ? 'status-running' : ''}`}>{agent.status}</span></button>
                <AgentCostReadout usage={state.nativeConnection.status === 'open' ? agent.usage : null}/></div>)}</div></details>}
          <details className="workspace-support-details"><summary>Usage in this chat</summary><TeamUsageSummary state={state} projection={projection}/></details>
          {state.nativeEvents.data && state.nativeEvents.data.events.length > 0 && <details className="workspace-support-details"><summary>Activity details</summary>
            <pre>{JSON.stringify(state.nativeEvents.data.events, null, 2)}</pre></details>}
          </div> : null}
        </>}</ResourceView>}
  </WorkspaceShell>;
}
